import { useState, useEffect } from 'react';

interface DisconnectQBModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  companyName?: string;
}

export function DisconnectQBModal({
  isOpen,
  onClose,
  onConfirm,
  companyName,
}: DisconnectQBModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setError(null);
      setIsDisconnecting(false);
    }
  }, [isOpen]);

  const isMatch = confirmText === 'DISCONNECT';

  const handleConfirm = async () => {
    if (!isMatch) return;

    setIsDisconnecting(true);
    setError(null);

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect QuickBooks');
      setIsDisconnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Disconnect QuickBooks</h3>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">
              We're sorry to see you go. Disconnecting will remove your QuickBooks access tokens,
              but we'll keep all your synced data (vendors, accounts, and invoices) in case you want
              to reconnect later.
            </p>

            {companyName && (
              <p className="text-sm text-gray-600 mb-3">
                You are disconnecting from:{' '}
                <span className="font-semibold text-gray-900">{companyName}</span>
              </p>
            )}

            <p className="text-sm text-gray-600 mb-3">
              If you reconnect in the future, we'll link back to the same QuickBooks company.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <p className="text-sm text-blue-800">
                You can always reconnect your QuickBooks account from this page.
              </p>
            </div>

            <p className="text-sm text-gray-600 mb-3">
              To confirm, type{' '}
              <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded">
                DISCONNECT
              </span>{' '}
              below:
            </p>

            {/* TODO: Future enhancement - add optional feedback form asking why disconnecting */}

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DISCONNECT"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono"
              autoFocus
            />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isDisconnecting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!isMatch || isDisconnecting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDisconnecting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Disconnecting...
                </span>
              ) : (
                'Disconnect QuickBooks'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
